import React, { useState, useEffect, useRef } from 'react';

/**
 * The core Overview-tab interaction: a value that renders as text by
 * default and swaps to an input on click.
 *
 *   Read mode: transparent button. Hover gets --bg-hover bg + faint
 *   border + a pencil glyph in text-4. Empty values render the
 *   placeholder. `pending` adds a small accent dot on the right.
 *
 *   Edit mode: <input> (or <textarea> if multiline). Accent border +
 *   3px ring. Autofocus. ESC restores read mode; Enter / blur commits.
 *
 * Props:
 *   value      — current value (committed; pending values are managed
 *                by the parent and reflected here)
 *   placeholder
 *   mono       — render value in Geist Mono with tabular numerals
 *   multiline  — textarea instead of input; commit on blur only
 *   prefix     — node rendered inside the chrome on the left (e.g.
 *                a calendar icon for dates)
 *   pending    — boolean; render the small accent dot
 *   type       — input type (text / date / number / ...)
 *   onChange(newValue) — committed value (called on Enter / blur)
 */
export default function InlineEdit({
  value = '',
  placeholder = '',
  mono = false,
  multiline = false,
  prefix = null,
  pending = false,
  type = 'text',
  big = false,
  onChange,
  ariaLabel,
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setLocal(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    if (local !== value) onChange && onChange(local);
    setEditing(false);
  };
  const cancel = () => {
    setLocal(value);
    setEditing(false);
  };

  const baseFont = mono ? 'var(--font-mono)' : 'var(--font-sans)';
  const tabularNum = mono ? 'tabular-nums' : 'normal';

  if (editing) {
    const sharedStyle = {
      width: '100%',
      padding: prefix ? '8px 10px 8px 30px' : '8px 10px',
      border: '1px solid var(--accent)',
      borderRadius: 8,
      background: 'var(--bg-surface)',
      color: 'var(--text-1)',
      fontFamily: baseFont,
      fontVariantNumeric: tabularNum,
      fontSize: big ? 15.5 : 14.5,
      fontWeight: big ? 600 : 400,
      letterSpacing: big ? '-0.015em' : '-0.005em',
      outline: 'none',
      boxShadow: '0 0 0 3px var(--accent-ring)',
      resize: multiline ? 'vertical' : 'none',
      boxSizing: 'border-box',
    };
    return (
      <div style={{ position: 'relative' }}>
        {prefix && (
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: multiline ? 10 : '50%',
              transform: multiline ? undefined : 'translateY(-50%)',
              color: 'var(--text-4)',
              display: 'inline-flex',
              pointerEvents: 'none',
            }}
          >
            {prefix}
          </span>
        )}
        {multiline ? (
          <textarea
            ref={inputRef}
            rows={3}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } }}
            style={sharedStyle}
            aria-label={ariaLabel}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              else if (e.key === 'Escape') { e.stopPropagation(); cancel(); }
            }}
            style={sharedStyle}
            aria-label={ariaLabel}
          />
        )}
      </div>
    );
  }

  const isEmpty = value == null || value === '';
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        padding: prefix ? '8px 28px 8px 30px' : '8px 10px',
        border: `1px solid ${hover ? 'var(--border-subtle)' : 'transparent'}`,
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: isEmpty ? 'var(--text-4)' : 'var(--text-1)',
        fontFamily: baseFont,
        fontVariantNumeric: tabularNum,
        fontSize: big ? 15.5 : 14.5,
        fontWeight: isEmpty ? 400 : (big ? 600 : 500),
        letterSpacing: big ? '-0.015em' : '-0.005em',
        textAlign: 'left',
        borderRadius: 8,
        cursor: 'text',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'background .12s, border-color .12s',
        minHeight: multiline ? 56 : 'auto',
      }}
    >
      {prefix && (
        <span
          style={{
            position: 'absolute',
            left: 10,
            top: multiline ? 10 : '50%',
            transform: multiline ? undefined : 'translateY(-50%)',
            color: 'var(--text-4)',
            display: 'inline-flex',
            pointerEvents: 'none',
          }}
        >
          {prefix}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {isEmpty ? placeholder : value}
      </span>
      {/* Pencil glyph appears on hover (text-4) so users discover edit */}
      {hover && !pending && (
        <span
          aria-hidden
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-4)', display: 'inline-flex' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z"/></svg>
        </span>
      )}
      {/* Pending indicator */}
      {pending && (
        <span
          aria-hidden
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            width: 6, height: 6, borderRadius: 999, background: 'var(--accent)',
          }}
        />
      )}
    </button>
  );
}
